import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useOssAuth } from "../../context/OssAuthContext";
import OssSectionPageHeader from "../../components/oss/OssSectionPageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIE = [
  { value: "idraulico", label: "Idraulico" },
  { value: "elettrico", label: "Elettrico" },
  { value: "edile", label: "Edile" },
  { value: "informatico", label: "Informatico" },
  { value: "impianti", label: "Impianti" },
  { value: "altro", label: "Altro" },
];

const emptyForm = {
  nome: "",
  telefono: "",
  email: "",
  categorie: [],
  note: "",
  attivo: true,
};

export default function OssManutentori() {
  const { token, activeOss } = useOssAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const canAccess = Boolean(activeOss?.responsabile);

  const load = useCallback(async () => {
    if (!token || !canAccess) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/oss/me/manutenzioni/fornitori`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento manutentori");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, canAccess]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canAccess) {
    return <Navigate to="/oss" replace />;
  }

  const headers = { Authorization: `Bearer ${token}` };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (f) => {
    setEditId(f.id);
    setForm({
      nome: f.nome || "",
      telefono: f.telefono || "",
      email: f.email || "",
      categorie: f.categorie || [],
      note: f.note || "",
      attivo: f.attivo !== false,
    });
    setOpen(true);
  };

  const toggleCat = (value) => {
    setForm((prev) => ({
      ...prev,
      categorie: prev.categorie.includes(value)
        ? prev.categorie.filter((c) => c !== value)
        : [...prev.categorie, value],
    }));
  };

  const save = async () => {
    if (!form.nome.trim()) {
      toast.error("Nome obbligatorio");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await axios.patch(
          `${API_URL}/api/oss/me/manutenzioni/fornitori/${editId}`,
          form,
          { headers }
        );
        toast.success("Manutentore aggiornato");
      } else {
        await axios.post(`${API_URL}/api/oss/me/manutenzioni/fornitori`, form, { headers });
        toast.success("Manutentore creato");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo manutentore?")) return;
    try {
      await axios.delete(`${API_URL}/api/oss/me/manutenzioni/fornitori/${id}`, { headers });
      toast.success("Eliminato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div className="space-y-4">
      <OssSectionPageHeader
        sectionKey="manutentori"
        title="Manutentori"
        subtitle="Anagrafica fornitori e manutentori della struttura"
      />

      <Card className="border-orange-200">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <Building2 className="w-5 h-5" />
            Elenco manutentori
          </CardTitle>
          <Button size="sm" onClick={openCreate} className="bg-orange-600 hover:bg-orange-700">
            <Plus className="w-4 h-4 mr-1" />
            Aggiungi
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">Nessun manutentore registrato</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((f) => (
                <li key={f.id} className="py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-800">{f.nome}</p>
                      {!f.attivo && (
                        <Badge variant="outline" className="text-slate-500">Inattivo</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {[f.telefono, f.email].filter(Boolean).join(" · ") || "Nessun contatto"}
                    </p>
                    {(f.categorie || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {f.categorie.map((c) => (
                          <Badge key={c} className="bg-orange-50 text-orange-800 border-orange-200">
                            {CATEGORIE.find((x) => x.value === c)?.label || c}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(f)}>
                      <Pencil className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(f.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Modifica manutentore" : "Nuovo manutentore"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Nome *</Label>
              <Input
                value={form.nome}
                onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Telefono</Label>
              <Input
                value={form.telefono}
                onChange={(e) => setForm((p) => ({ ...p, telefono: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Categorie</Label>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIE.map((c) => (
                  <label key={c.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={form.categorie.includes(c.value)}
                      onCheckedChange={() => toggleCat(c.value)}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label>Note</Label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                rows={2}
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={form.attivo}
                onCheckedChange={(v) => setForm((p) => ({ ...p, attivo: Boolean(v) }))}
              />
              Attivo
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Annulla
            </Button>
            <Button onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
